import React, { useState, useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import styled from "styled-components";
import isEqual from "lodash/isEqual";
import ToggleSwitch from "../../../../../components/common/switchToggle/ToggleSwitch";
import { setColor } from "../../../../../assets/styles/styles";
import Button from "../../../../../components/common/button/Button";
import Typography from "../../../../../components/common/text/Typography";
import { addRoomPreset, updateRoomPreset, getAllKeybinds } from "../../../../../api/ClientsTagController";
import Banner from "../../../../../components/layout/ui/Banner";
import JsonViewerModal from "../../vanityLinks/components/JsonViewerModal";
import Modal from "../../../components/Modal";
import { DEFAULT_QUERY_OPTIONS } from "../../../../../constants";

const RoomPresetModal = ({ rowData, closeModal }) => {
    // banner related states
    const [successMessageBanner, setSuccessMessageBanner] = useState("");
    const [bannerType, setBannerType] = useState("SUCCESS");

    const [buttonDisabled, setButtonDisabled] = useState(true);
    const [newKey, setNewKey] = useState("");
    const [newValue, setNewValue] = useState("");

    const [isJsonEditorOpen, setIsJsonEditorOpen] = useState(false);
    const [jsonEditorValue, setJsonEditorValue] = useState("");

    const [keyOptions, setKeyOptions] = useState([]);
    const [valueOptions, setValueOptions] = useState([]);

    const { data: keybindsData, isLoading: roomPresetsLoading } = useQuery(
        ["keybindsOptions"], // Query key, add params if needed
        () => getAllKeybinds(), // Query function
        {
            ...DEFAULT_QUERY_OPTIONS(),
        }
    );

    const [presetState, setPresetState] = useState({
        presetName: "",
        canReturnToNavMenuEnabled: false,
        doFormsExist: false,
        ecommerceEnabled: false,
        leaderboardsEnabled: false,
        otherConfig: [],
        presetKeybinds: {},
        settingsMenuEnabled: false,
        showEmoteWheel: false,
        showFriends: false,
        spotifyEnabled: false,
        textChatEnabled: false,
        voiceChatEnabled: false,
    });

    const initialPresetStateRef = useRef();

    const handleChange = (key, value) => {
        if (key === "presetName" && value.trim() === "") {
            setButtonDisabled(true);
        } else {
            setButtonDisabled(false);
        }

        setPresetState((prev) => ({
            ...prev,
            [key]: value,
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!presetState.presetName) {
            setSuccessMessageBanner("Preset Name is required");
            setBannerType("ERROR");
            return;
        }

        try {
            let res;
            if (rowData) {
                const keybindsUnchanged = isEqual(
                    presetState.presetKeybinds,
                    initialPresetStateRef.current.presetKeybinds
                );

                // Create payload, omitting presetKeybinds if unchanged
                const { presetKeybinds, ...rest } = presetState;
                const payload = keybindsUnchanged ? rest : presetState;

                // PUT request for update
                res = await updateRoomPreset(rowData.presetId, payload);
            } else {
                // POST request for new
                res = await addRoomPreset(presetState);
            }

            if (res.status === 200 || res.status === 201) {
                setSuccessMessageBanner(
                    rowData ? "Room Preset Updated Successfully" : "Room Preset Added Successfully"
                );
                setBannerType("SUCCESS");

                setTimeout(() => {
                    closeModal();
                    window.location.reload();
                }, 1000);
            } else {
                console.error("Failed to submit room preset:", res);
                setSuccessMessageBanner("Failed to Submit Room Preset");
                setBannerType("ERROR");
            }

            console.log("Submitting Room Preset:", presetState);
        } catch (error) {
            console.error("Error submitting room preset:", error);
            setSuccessMessageBanner("Failed to Submit Room Preset");
            setBannerType("ERROR");
        }
    };

    // Helper to get used keys
    const usedKeys = Object.keys(presetState.presetKeybinds);
    const usedValues = Object.values(presetState.presetKeybinds);

    const handleAddKeybind = () => {
        if (newKey && newValue && !usedKeys.includes(newKey) && !usedValues.includes(newValue)) {
            setPresetState((prev) => ({
                ...prev,
                presetKeybinds: {
                    ...prev.presetKeybinds,
                    [newKey]: newValue,
                },
            }));
            setNewKey("");
            setNewValue("");
        }
    };

    const handleRemoveKeybind = (keyToRemove) => {
        setPresetState((prev) => {
            const { [keyToRemove]: _, ...remainingKeybinds } = prev.presetKeybinds;
            return {
                ...prev,
                presetKeybinds: remainingKeybinds,
            };
        });
    };

    const clearAllKeybinds = () => {
        setPresetState((prev) => ({
            ...prev,
            presetKeybinds: {},
        }));
        setNewKey("");
        setNewValue("");
    };

    useEffect(() => {
        if (rowData) {
            const state = {
                presetName: rowData.presetName || "",
                canReturnToNavMenuEnabled: rowData.canReturnToNavMenuEnabled || false,
                doFormsExist: rowData.doFormsExist || false,
                ecommerceEnabled: rowData.ecommerceEnabled || false,
                leaderboardsEnabled: rowData.leaderboardsEnabled || false,
                otherConfig: rowData.otherConfig || [],
                presetKeybinds: rowData.presetKeybinds || {},
                settingsMenuEnabled: rowData.settingsMenuEnabled || false,
                showEmoteWheel: rowData.showEmoteWheel || false,
                showFriends: rowData.showFriends || false,
                spotifyEnabled: rowData.spotifyEnabled || false,
                textChatEnabled: rowData.textChatEnabled || false,
                voiceChatEnabled: rowData.voiceChatEnabled || false,
            };
            setPresetState(state);
            initialPresetStateRef.current = state;
        } else {
            initialPresetStateRef.current = {
                presetName: "",
                canReturnToNavMenuEnabled: false,
                doFormsExist: false,
                ecommerceEnabled: false,
                leaderboardsEnabled: false,
                otherConfig: [],
                presetKeybinds: {},
                settingsMenuEnabled: false,
                showEmoteWheel: false,
                showFriends: false,
                spotifyEnabled: false,
                textChatEnabled: false,
                voiceChatEnabled: false,
            };
        }
    }, [rowData]);

    useEffect(() => {
        if (keybindsData) {
            setKeyOptions(keybindsData.data.keyOptions || []);
            setValueOptions(keybindsData.data.valueOptions || []);
        }
    }, [keybindsData]);

    useEffect(() => {
        if (!presetState.presetName.trim()) {
            setButtonDisabled(true);
            return;
        }

        if (!rowData) {
            setButtonDisabled(false);
            return;
        }

        const hasChanges = !isEqual(presetState, initialPresetStateRef.current);
        setButtonDisabled(!hasChanges);
    }, [presetState, rowData]);

    return (
        <>
            <Banner
                width={"100%"}
                zIndex="1000"
                message={`${successMessageBanner}`}
                type={bannerType}
                openBanner={!!successMessageBanner}
                setOpenBanner={setSuccessMessageBanner}
                topDisplay
                showCloseButton
                gameScreen={false}
                top={"0px"}
                left={"0"}
            />
            <ModalContent>
                <GeneralContainer>
                    <Typography variant="h2PurpleHeading" margin={`0 0 20px 0`}>
                        {rowData ? `Edit Room Preset` : `Add Room Preset`}
                    </Typography>
                    <Table onSubmit={handleSubmit}>
                        <TableRow>
                            <TableColumn>
                                <Label>Room Preset Name*</Label>
                                <InputBox
                                    type="text"
                                    id="roomPresetName"
                                    placeholder="Enter Room Preset Name"
                                    value={presetState.presetName}
                                    onChange={(e) => handleChange("presetName", e.target.value)}
                                />
                            </TableColumn>
                        </TableRow>
                        <TableRow>
                            <TableColumn>
                                <Label>Can return to navigation menu?</Label>
                                <ToggleSwitch
                                    value={presetState.canReturnToNavMenuEnabled}
                                    onChange={(value) => handleChange("canReturnToNavMenuEnabled", value)}
                                />
                            </TableColumn>
                        </TableRow>
                        <TableRow>
                            <TableColumn>
                                <Label>Do forms exist?</Label>
                                <ToggleSwitch
                                    value={presetState.doFormsExist}
                                    onChange={(value) => handleChange("doFormsExist", value)}
                                />
                            </TableColumn>
                        </TableRow>
                        <TableRow>
                            <TableColumn>
                                <Label>Ecommerce enabled?</Label>
                                <ToggleSwitch
                                    value={presetState.ecommerceEnabled}
                                    onChange={(value) => handleChange("ecommerceEnabled", value)}
                                />
                            </TableColumn>
                        </TableRow>
                        <TableRow>
                            <TableColumn>
                                <Label>Leaderboards enabled?</Label>
                                <ToggleSwitch
                                    value={presetState.leaderboardsEnabled}
                                    onChange={(value) => handleChange("leaderboardsEnabled", value)}
                                />
                            </TableColumn>
                        </TableRow>

                        <TableRow>
                            <TableColumn>
                                <Label>Other Config</Label>
                                <InputBox
                                    type="text"
                                    name={"otherConfig"}
                                    value={JSON.stringify(presetState.otherConfig || {})}
                                    onClick={() => {
                                        setIsJsonEditorOpen(true);
                                        setJsonEditorValue(JSON.stringify(presetState.otherConfig || {}, null, 2));
                                    }}
                                />
                            </TableColumn>
                        </TableRow>

                        <TableRow>
                            <TableColumn>
                                <Label>Preset Key binds</Label>
                                <KeybindContainer>
                                    <SelectBox value={newKey} onChange={(e) => setNewKey(e.target.value)}>
                                        <option value="">Select Key</option>
                                        {keyOptions
                                            .filter((key) => !usedKeys.includes(key))
                                            .map((key) => (
                                                <option key={key} value={key}>
                                                    {key}
                                                </option>
                                            ))}
                                    </SelectBox>
                                    <SelectBox value={newValue} onChange={(e) => setNewValue(e.target.value)}>
                                        <option value="">Select Action</option>
                                        {valueOptions
                                            .filter((val) => !usedValues.includes(val))
                                            .map((val) => (
                                                <option key={val} value={val}>
                                                    {val}
                                                </option>
                                            ))}
                                    </SelectBox>
                                    <Button
                                        type="button"
                                        variant={"SmallButton"}
                                        label={"+"}
                                        onClick={handleAddKeybind}
                                        disabled={
                                            !newKey ||
                                            !newValue ||
                                            usedKeys.includes(newKey) ||
                                            Object.keys(presetState.presetKeybinds).length >= 8
                                        }
                                    />
                                </KeybindContainer>
                                <KeybindList>
                                    {Object.keys(presetState.presetKeybinds).length >= 8 && (
                                        <Typography
                                            variant="pWhite"
                                            margin={"0"}
                                            fontSize={"18px"}
                                            color={`${setColor.redLightWarning}`}
                                            align={"left"}
                                            letterSpacing={"0px"}
                                        >
                                            You can only add up to 8 keybinds.
                                        </Typography>
                                    )}
                                    {Object.entries(presetState.presetKeybinds).map(([key, val]) => (
                                        <KeybindItem key={key}>
                                            {key} → {val}
                                            <Button
                                                variant={"SmallButton"}
                                                margin={"0 0 0 10px"}
                                                label="-"
                                                onClick={() => handleRemoveKeybind(key)}
                                            />
                                        </KeybindItem>
                                    ))}
                                </KeybindList>
                                <Button
                                    type="button"
                                    variant={"SmallButton"}
                                    margin={"10px 0 0 0"}
                                    label="Clear All"
                                    onClick={() => clearAllKeybinds()}
                                />
                            </TableColumn>
                        </TableRow>

                        <TableRow>
                            <TableColumn>
                                <Label>Settings menu enabled?</Label>
                                <ToggleSwitch
                                    value={presetState.settingsMenuEnabled}
                                    onChange={(value) => handleChange("settingsMenuEnabled", value)}
                                />
                            </TableColumn>
                        </TableRow>
                        <TableRow>
                            <TableColumn>
                                <Label>Show Emote Wheel?</Label>
                                <ToggleSwitch
                                    value={presetState.showEmoteWheel}
                                    onChange={(value) => handleChange("showEmoteWheel", value)}
                                />
                            </TableColumn>
                        </TableRow>
                        <TableRow>
                            <TableColumn>
                                <Label>Show Friends?</Label>
                                <ToggleSwitch
                                    value={presetState.showFriends}
                                    onChange={(value) => handleChange("showFriends", value)}
                                />
                            </TableColumn>
                        </TableRow>
                        <TableRow>
                            <TableColumn>
                                <Label>Spotify enabled?</Label>
                                <ToggleSwitch
                                    value={presetState.spotifyEnabled}
                                    onChange={(value) => handleChange("spotifyEnabled", value)}
                                />
                            </TableColumn>
                        </TableRow>
                        <TableRow>
                            <TableColumn>
                                <Label>Text chat enabled?</Label>
                                <ToggleSwitch
                                    value={presetState.textChatEnabled}
                                    onChange={(value) => handleChange("textChatEnabled", value)}
                                />
                            </TableColumn>
                        </TableRow>
                        <TableRow>
                            <TableColumn>
                                <Label>Voice chat enabled?</Label>
                                <ToggleSwitch
                                    value={presetState.voiceChatEnabled}
                                    onChange={(value) => handleChange("voiceChatEnabled", value)}
                                />
                            </TableColumn>
                        </TableRow>
                        <TableRow>
                            <Button
                                type="submit"
                                variant={"PinkButton"}
                                buttonWidth={"100%"}
                                disabled={buttonDisabled}
                                label={rowData ? `Edit Room Preset` : `Add Room Preset`}
                            />
                        </TableRow>
                    </Table>
                </GeneralContainer>
            </ModalContent>
            {isJsonEditorOpen && (
                <Modal
                    isOpen={isJsonEditorOpen}
                    setIsOpen={setIsJsonEditorOpen}
                    children={
                        <JsonViewerModal
                            jsonEditorValue={jsonEditorValue}
                            handleJsonSave={(data) => {
                                setPresetState((prev) => ({
                                    ...prev,
                                    otherConfig: data,
                                }));
                                setIsJsonEditorOpen(false);
                            }}
                            fieldName={"otherConfig"}
                            label={"Other Config"}
                            closeModal={() => setIsJsonEditorOpen(false)}
                        />
                    }
                />
            )}
        </>
    );
};

export default RoomPresetModal;

const ModalContent = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    width: 100%;
    color: ${setColor.inputText};
`;

const GeneralContainer = styled.div`
    width: 100%;
    font-family: "Barlow-Regular", Helvetica;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
    @media (max-width: 768px) {
        font-size: 14px;
    }
`;

const Table = styled.form`
    display: flex;
    flex-direction: column;
    padding: 1rem;
    background: ${setColor.passwordValidBox};
    border-radius: 5px;
    margin-top: 15px;
`;

const TableRow = styled.div`
    display: flex;
    flex-direction: row;
    margin-bottom: 10px;
`;

const TableColumn = styled.div`
    flex: 1;
    padding: 5px;
`;

const Label = styled.div`
    font-weight: 600;
    font-size: 16px;
    margin-bottom: 10px;
`;

const InputBox = styled.input`
    padding: 10px;
    border: ${(props) => (props.errorMessage ? `1px solid ${setColor.errorColor}` : `1px solid ${setColor.grey1}`)};
    border-radius: 5px;
    color: ${setColor.inputText};
    margin: 0rem 0rem 0rem 0rem;
    width: ${(props) => (props.width ? props.width : "100%")};
    background-color: transparent;
    margin-bottom: ${(props) => (props.errorMessage ? "0.5rem" : "0rem")};
    &:disabled {
        background-color: ${setColor.grey1};
    }
`;

const KeybindContainer = styled.div`
    display: flex;
    gap: 8px;
    align-items: center;
    margin-bottom: 10px;
`;

const SelectBox = styled.select`
    padding: 10px 12px;
    border: 1px solid ${setColor.grey1};
    border-radius: 5px;
    color: ${setColor.inputText};
    background-color: ${setColor.inputBgColor};
    font-family: "Barlow-Regular", Helvetica;
    font-size: 14px;
    width: 180px;
    cursor: pointer;
    appearance: none;
    background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23FBFCFF' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
    background-repeat: no-repeat;
    background-position: right 10px center;
    background-size: 16px;
    padding-right: 36px;
    
    &:focus {
        outline: none;
        border-color: ${setColor.accent4};
        box-shadow: 0 0 0 2px ${setColor.purpleDark};
    }
    
    &:hover {
        border-color: ${setColor.accent4};
    }

    option {
        background-color: ${setColor.solidDarkPurpleBackground};
        color: ${setColor.inputText};
        padding: 12px;
        font-family: "Barlow-Regular", Helvetica;
        font-size: 14px;
    }

    option:hover,
    option:focus,
    option:checked {
        background-color: ${setColor.accent4};
        color: ${setColor.black};
    }
`;

const KeybindList = styled.ul`
    list-style: none;
    padding: 0;
    margin: 0;
`;

const KeybindItem = styled.li`
    font-family: "Barlow-Regular", Helvetica;
    font-size: 14px;
    color: ${setColor.inputText};
    margin-bottom: 5px;
    padding: 5px 0;
`;
